import { html, nothing, PropertyValueMap, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksList } from '../list/list';
import { BlocksPrimaryNavItem } from '../primary-nav-item/primary-nav-item';
import { lockBodyScrolling, unlockBodyScrolling } from '../../util/scroll';
import type { PrimaryNavBehaviorVariant } from './primary-nav.model';
import styles from './primary-nav.styles';

/**
 * @deprecated - This component `cat-primary-nav` has been deprecated and the updated version will be available with name `cat-primary-nav-labs`. New component will be found under labs folder from Minor Release 3.10.0, `cat-primary-nav` will be removed in 5.0 major release.
 * @slot nav-list - The primary navigation items
 * @slot before - Content appearing before the default slot
 * @slot after - Content appearing after the default slot
 * @csspart base - The component's base wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart list - The component's list.
 * @csspart part-default - The component's default slot.
 * @csspart more - The component's more.
 * @csspart more-dropdown - The component's more dropdown list.
 * @csspart part-after - The component's after slot.
 * @csspart item - The component's `<cat-list-item>`.
 * @csspart sublist-items - The component's nested `<cat-list>`.
 * @csspart sub-sub-item - The component's nested cat-list-item inside nested cat-list.
 * @csspart part-custom-link - The component's custom link as list item anchor tag in more dropdown
 */
export class BlocksPrimaryNav extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * is Collapsed
   * 1) Primary Nav is display none set to false at mobile view. Open as overlay at mobile when set to true
   */
  @property({ type: Boolean })
  isCollapsed: boolean | undefined;

  /**
   * Show more dropdown menu label text
   */
  @property({ type: String }) moreDropdownLabel = 'More';

  /**
   * Behavior variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**more** shows the more dropdown menu for items when priority exists for the first several icons before
   * breaking on smaller screens</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  behavior = 'more';

  /**
   * Dropdown panel behavior variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>By default, an accordion is rendered for these</li>
   * <li>**flyout** renders a dropdown that contains all flyouts</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  dropdownPanelBehavior: PrimaryNavBehaviorVariant;

  /**
   * **Breakpoint below which primary-nav switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Provides an option for primary nav item to use 100% available space
   * Fullwidth is applicable only for non-more nav items.
   * It can be used with lesser primary-nav options than viewport width
   */
  @property({ type: Boolean })
  fullWidth = false;

  @state()
  private isMobile = false;

  /**
   * **Query the primaryNav element**
   */
  @query('.cat-c-primary-nav') private primaryNav!: HTMLElement;

  /**
   * **Query the more dropdown list element**
   */
  @query('.cat-c-primary-nav__more-dropdown') private moreDropdown: BlocksList | undefined;

  /**
   * **Query the more element**
   */
  @query('.cat-c-primary-nav__more') private moreNavItem: BlocksPrimaryNavItem | undefined;

  /**
   * Too keep current focused element.
   */
  private focusedElement: BlocksPrimaryNavItem | null = null;

  protected slots = new SlotController(this, 'before', 'after', null);

  /**
   * **Query the primaryNavItems from light dom**
   */
  private get primaryNavItems(): BlocksPrimaryNavItem[] {
    return Array.from(this.querySelectorAll('cat-primary-nav-item'));
  }

  /**
   * **Query the primaryNavItems from light dom along with more item**
   */
  private get visiblePrimaryNavItems(): BlocksPrimaryNavItem[] {
    let items = this.primaryNavItems;

    if (this.moreNavItem && this.moreDropdownListItemsVisible.length > 0) {
      items.push(this.moreNavItem);
    }

    const visiblePrimaryItem = items.filter((item) => !item.classList.contains('cat-is-hidden'));
    return visiblePrimaryItem;
  }

  /**
   * **Query the hidden primaryNavItems from light dom**
   */
  private get primaryHiddenNavListItems(): BlocksPrimaryNavItem[] {
    return Array.from(this.querySelectorAll('cat-primary-nav-item.cat-is-hidden'));
  }

  /**
   * **Query all the visible listItems inside more dropdown list element**
   */
  private get moreDropdownListItemsVisible(): Array<BlocksListItem> {
    return Array.from(this.moreDropdown?.querySelectorAll<BlocksListItem>('.cat-c-primary-nav__more-dropdown-item.cat-is-visible')!);
  }

  /**
   * **Query all the listItems inside more dropdown**
   */
  private get moreDropdownListItems(): Array<BlocksListItem> {
    return Array.from(this.moreDropdown?.querySelectorAll<BlocksListItem>('.cat-c-primary-nav__more-dropdown-item')!);
  }

  /**
   * **Boolean to check whether next set of iteration required for width calc**
   */
  private updateRequired = false;

  /**
   * **Used for debounce in window resize**
   */
  private clearSetTimeout?: ReturnType<typeof setTimeout>;

  /**
   * **Boolean to check whether nav-item width calculated**
   */
  private widthCalculated = false;

  private widthCalcResolve!: () => void;
  private widthCalcPromise = new Promise<void>((resolve) => {
    this.widthCalcResolve = resolve;
  });

  constructor() {
    super();
    this.addEventListener('bl-primarynav-item-panel-open', this.onPrimaryNavItemPanelOpen);
    this.addEventListener('bl-primarynav-item-panel-close', this.onPrimaryNavItemPanelClose);
  }

  /**
   * Connected callback lifecycle
   * 1) Add window resize listener
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.onResize); /* 1 */
    // Re calculate here only if component has already updated. Will not run on first cycle
    if (this.hasUpdated) {
      this.syncSubnavListAndDropdown();
    }
  }
  /**
   * Disconnected callback lifecycle
   * 2) Remove window resize event listener
   */
  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize); /* 2 */
    super.disconnectedCallback();
    unlockBodyScrolling(this); //Remove body scroll lock when disconnected
  }

  /**
   * First updated lifecycle when component is first updated on page
   */
  protected async firstUpdated() {
    this.setMobile();
    let items = this.primaryNavItems;
    if (this.moreNavItem) {
      items.push(this.moreNavItem);
    }
    this.focusContent(items[0], items, false);
  }

  /**
   * Resize handler
   */
  private onResize = () => {
    this.syncSubnavListAndDropdown();
    this.collapsePrimaryNavItems();
  };

  /**
   * Updated lifecycle hook
   * 1) used document.fonts.ready since all primary-nav-items clientHeight is calculated before
    the font-family is ready, so the width of the items increases after the calculation. So, used this API to
    calculate the width only after the fonts are ready.
    https://developer.mozilla.org/en-US/docs/Web/API/Document/fonts
   */

  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    if (_changedProperties.has('behavior')) {
      await Promise.all([document.fonts.ready, this.updateComplete]); /* 1 */
      this.syncSubnavListAndDropdown();
    }

    if (_changedProperties.has('moreDropdownLabel')) {
      this.onSlotChange();
    }

    if (_changedProperties.has('isCollapsed') || _changedProperties.has('isMobile')) {
      if (!this.isMobile){
        unlockBodyScrolling(this);
        return;
      }
      this.handleBodyScroll();
    }
  }

  /**
   * Handle body scroll locking
   */
  private handleBodyScroll(){
    if (this.isCollapsed){
      lockBodyScrolling(this, 'auto');
    }
    else{
      unlockBodyScrolling(this);
    }
  }

  /**
   * Make dropdown items visible
   * 1) Make all the primaryNavItems visible
   * 2) Make all the list-items inside the more dropdown as hidden
   * 3) Requesting update manually since no reactive property is changed
   * 4) Calculate the width of all the primaryNavItems
   */
  private async onSlotChange(): Promise<void> {
    this.primaryNavItems.forEach((primaryNavItem) => {
      if (primaryNavItem.classList.contains('cat-is-hidden')) {
        primaryNavItem.classList.remove('cat-is-hidden');
      }
    }); /* 1 */
    this.moreDropdownListItems.forEach((listItem) => {
      if (listItem.classList.contains('cat-is-visible')) {
        listItem.classList.remove('cat-is-visible');
        listItem.classList.add('cat-is-invisible');
      }
    }); /* 2 */
    this.requestUpdate(); /* 3 */
    await this.updateComplete;
    this.applyDataWidths(); /* 4 */
    await this.updateComplete;
    this.syncSubnavListAndDropdown();
  }

  /**
   * Make dropdown items visible
   * 1) Wait for the update to be complete
   * 2) Loop through primary nav list items
   * 3) If the index of the primary nav list items contains hidden class, remove invisible class and apply visible class to same index in
   * dropdown list items
   * 4) If the index of the primary nav list items don't contain hidden class, remove visible class and apply invisible class to same index in
   * dropdown list items
   */
  private async applyDropdownItemsVisible() {
    await this.updateComplete; /* 1 */
    const moreNavItem = this.shadowRoot?.querySelector<BlocksPrimaryNavItem>('.cat-c-primary-nav__more');

    /* 2 */
    const dropdownList = this.shadowRoot?.querySelector('.cat-c-primary-nav__more-dropdown');
    const dropdownListItems = dropdownList?.querySelectorAll('.cat-c-primary-nav__more-dropdown-item');
    const primaryNavListItems = this.querySelectorAll('cat-primary-nav-item');

    const navListElements = this.shadowRoot?.querySelector<HTMLSlotElement>('.nav-list')?.assignedElements() as HTMLElement[];

    const primaryNavItem = Array.from(navListElements).filter((node) => {
      return node.tagName.toUpperCase() === 'CAT-PRIMARY-NAV-ITEM';
    }) as BlocksPrimaryNavItem[];

    primaryNavItem.forEach((_, index: number) => {
      primaryNavListItems[index].setAttribute('data-width', primaryNavListItems[index].clientWidth.toString());
      if (!primaryNavListItems[index]?.classList.contains('cat-is-hidden')) {
        dropdownListItems?.[index].setAttribute('data-width', this.primaryNavItems[index].clientWidth.toString());
      }
      const dropdownListItemsIndex = dropdownListItems?.[index];
      if (dropdownListItemsIndex) {
        if (primaryNavItem[index]?.classList.contains('cat-is-hidden')) {
          /* 3 */
          dropdownListItemsIndex.classList.remove('cat-is-invisible');
          dropdownListItemsIndex.classList.add('cat-is-visible');
          if (moreNavItem && primaryNavItem[index].isCurrent) {
            moreNavItem.isCurrent = true;
            (dropdownListItemsIndex as BlocksListItem).isCurrent = true;
          }
        } else {
          /* 4 */
          dropdownListItemsIndex.classList.remove('cat-is-visible');
          dropdownListItemsIndex.classList.add('cat-is-invisible');
          if (moreNavItem && (dropdownListItemsIndex as BlocksListItem).isCurrent) {
            moreNavItem.isCurrent = false;
            primaryNavItem[index].isCurrent = true;
          }
        }
      }
    });
  }

  /**
   * Apply data widths to both the primary nav list items and dropdown list items
   */
  private applyDataWidths = async () => {
    await this.updateComplete; /* 1 */
    /* 2 */
    const dropdownList = this.shadowRoot?.querySelector('.cat-c-primary-nav__more-dropdown');
    const dropdownListItems = dropdownList?.querySelectorAll('.cat-c-primary-nav__more-dropdown-item');

    const navListElements = this.shadowRoot?.querySelector<HTMLSlotElement>('.nav-list')?.assignedElements() as HTMLElement[];

    const primaryNavItem = Array.from(navListElements).filter((node) => {
      return node.tagName.toUpperCase() === 'CAT-PRIMARY-NAV-ITEM';
    }) as BlocksPrimaryNavItem[];

    primaryNavItem.forEach((_, index: number) => {
      this.primaryNavItems[index].setAttribute('data-width', this.primaryNavItems[index].clientWidth.toString());

      dropdownListItems?.[index].setAttribute('data-width', this.primaryNavItems[index].clientWidth.toString());
    });
    this.widthCalculated = true;
  };

  /**
   * Update subnav list
   * 1) Wait for the update to be complete
   * 2) Loop through primary nav list items to calculate width without more button
   * 3) Calculate the available space that the more nav item and slot contents takes up
   * 4) Set the data width of the more button to the more button
   * 5) If that nav width is greater than the nav children minus more button width,
   * hide the last child that isn't the more button. applyDropdownVisible adds to the
   * dropdown list
   * 6) Otherwise,
   *          i ) If there is only one visible item in dropdown list and if it can be accommodated when removing more dropdown, hide dropdown and display nav item
   *          ii ) show the last item next to the more button and hide item in dropdown list
   * 7) If there are items in the more dropdown, show the more button
   * 8) If step 5 or step 6 is executed, then updateRequired is set to true, to indicate next set of iteration is needed for width calc.
   */
  private updateSubnavList = async () => {
    await this.updateComplete; /* 1 */

    const hiddenChildren = this.querySelectorAll('cat-primary-nav-item.cat-is-hidden:not(.cat-c-primary-nav__more)');
    const children = this.querySelectorAll('cat-primary-nav-item:not(.cat-c-primary-nav__more):not(.cat-is-hidden)');
    const dropdownList = this.shadowRoot?.querySelector('.cat-c-primary-nav__more-dropdown');
    const moreNavItem = this.shadowRoot?.querySelector('.cat-c-primary-nav__more')!;

    let navWidth = 0;
    const moreNavItemWidth = moreNavItem.clientWidth;

    children.forEach((child) => {
      /* 2 */
      const childWidth = child.clientWidth;
      navWidth += childWidth;
    });

    const availableSpace =
      (this.clientWidth || this.primaryNav.clientWidth) - this.slotWidth('before') - this.slotWidth('after') - moreNavItemWidth; /* 3 */

    moreNavItem.setAttribute('data-width', moreNavItemWidth.toString()); /* 4 */

    if (navWidth >= availableSpace) {
      const lastItem = children[children.length - 1];
      if (lastItem) {
        lastItem.classList.add('cat-is-hidden'); /* 5 */
        this.updateRequired = true; /* 8 */
      }
    } else {
      const firstMoreElement = <HTMLElement>dropdownList?.querySelector('.cat-is-visible');

      if (firstMoreElement) {
        let navWidthAndDataWidth = navWidth;
        const dataWidthElement = firstMoreElement.getAttribute('data-width');
        if (dataWidthElement) {
          navWidthAndDataWidth += parseInt(dataWidthElement);
        }
        if (
          (this.moreDropdownListItemsVisible.length === 1 && navWidthAndDataWidth < availableSpace + moreNavItemWidth) ||
          navWidthAndDataWidth < availableSpace
        ) {
          this.updateRequired = true; /* 8 */
          hiddenChildren[0].classList.remove('cat-is-hidden');
          this.moreDropdownListItemsVisible[0].classList.remove('cat-is-visible'); /* 6 */
        }
      }
    }
    this.applyDropdownItemsVisible();
    await this.updateComplete;
    if (this.moreDropdownListItemsVisible.length > 0) {
      moreNavItem.classList.add('cat-is-collapsed'); /* 7 */
    } else {
      moreNavItem.classList.remove('cat-is-collapsed');
    }
    if (this.updateRequired) {
      this.updateRequired = false;
      this.updateSubnavList();
    }
  };

  /**
   * Sync the subnav list with the dropdown list
   * 1) When the subnav list hides an item, apply the visible class to the dropdown for that
   * item to show up.
   * 2) Switch to mobile view if screen size is below mobileBreakPoint property.
   */
  private syncSubnavListAndDropdown = async () => {
    await this.updateComplete;
    if (this.clearSetTimeout) {
      clearTimeout(this.clearSetTimeout);
    }
    this.clearSetTimeout = setTimeout(async () => {
      clearTimeout(this.clearSetTimeout);
      this.setMobile();
      await Promise.all([this.updateComplete, this.widthCalcPromise]);
      if (this.behavior === 'more' && !this.isMobile) {
        /* 1 */
        // Added setTimeout for paint delay from mobile to desktop
        setTimeout(() => {
          this.updateRequired = false;

          if (!this.fullWidth) {
            this.updateSubnavList();
          }
        }, 0);
      }
    }, 200);
  };

  /**
   * Collapse the Primary Nav Item and More Nav Item when it is Active
   */
  private collapsePrimaryNavItems = async () => {
    await this.updateComplete;
    const moreNavItem = this.shadowRoot?.querySelector<BlocksPrimaryNavItem>('.cat-c-primary-nav__more');
    this.primaryNavItems?.forEach((primaryNavItem) => {
      if (primaryNavItem.isActive) {
        primaryNavItem.isActive = false;
      }
    });
    if (moreNavItem?.isActive) {
      moreNavItem.isActive = false;
    }
  };

  /**
   * Set isMobile property on window resize
   * 1) When window width is lesser than `mobileBreakPoint` props, set isMobile to true.
   * 2) When window width is greater than `mobileBreakPoint` props, set isMobile to false.
   * 3) When the device is mobile, cat-c-primary-nav position is set to absolute.
     But, due to the css prop 'flex:1', primaryNav host takes up space ,which creates layout issues. 
     So, removing it and will be added again when device is not mobile.
   */

  private async setMobile() {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    await this.updateComplete;
    if (width < this.mobileBreakPoint) {
      this.isMobile = true; /* 1 */
      this.setAttribute('isMobile', ''); /* 3 */
    } else {
      this.isMobile = false; /* 2 */
      this.removeAttribute('isMobile');
      if (!this.widthCalculated && this.behavior === 'more') {
        setTimeout(async () => {
          await document.fonts.ready;
          this.applyDataWidths();
          await this.updateComplete;
          this.widthCalcResolve();
        }, 0);
      }
    }
    this.primaryNavItems.forEach((catPrimaryNavItem: BlocksPrimaryNavItem) => {
      catPrimaryNavItem.isMobile = this.isMobile;
    });
  }

  /**
   * method is used on keydown actions.
   * @param e
   */
  private handleOnKeyDown(e: KeyboardEvent) {
    const items = this.visiblePrimaryNavItems;
    const isActiveItem: boolean = this.hasActiveItem(items);
    if (isActiveItem) {
      return;
    }

    if (this.isMobile && (e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW)) {
      e.preventDefault();
      e.key === KEYS.DOWN_ARROW ? this.moveFocusToNextItem(items) : this.moveFocusToPreviousItem(items);
    } else if (!this.isMobile && (e.key === KEYS.RIGHT_ARROW || e.key === KEYS.LEFT_ARROW)) {
      e.preventDefault();
      e.key === KEYS.RIGHT_ARROW ? this.moveFocusToNextItem(items) : this.moveFocusToPreviousItem(items);
    }
  }

  /**
   * method is used to focus next item on right arrow click
   * @param items
   */
  private moveFocusToNextItem(items: BlocksPrimaryNavItem[]) {
    if (!this.focusedElement) {
      return;
    }
    let currentIndex = Array.from(items).indexOf(this.focusedElement);
    const nextIndex = (currentIndex + 1) % items.length;
    this.focusContent(items[nextIndex], items, true);
  }

  /**
   * method is used to focus previous item on left arrow click
   * @param items
   */
  private moveFocusToPreviousItem(items: BlocksPrimaryNavItem[]) {
    if (!this.focusedElement) {
      return;
    }
    const currentIndex = Array.from(items).indexOf(this.focusedElement);
    const prevIndex = (currentIndex - 1 + items.length) % items.length;
    this.focusContent(items[prevIndex], items, true);
  }

  /**
   * method is used to check any item in primary nav is active.
   */
  private hasActiveItem(items: BlocksPrimaryNavItem[]): boolean {
    for (const item of items) {
      const activeElement = item.shadowRoot?.querySelector('.cat-is-active');
      if (activeElement) {
        return true;
      }
    }
    return false;
  }

  /**
   * Method is used to set tabindex and focus.
   */
  private focusContent(item: HTMLElement, navItems: BlocksPrimaryNavItem[], setFocus?: boolean) {
    navItems.forEach((navItem) => {
      setTimeout(() => {
        const navItemElement = navItem.shadowRoot?.querySelector('.cat-c-primary-nav__link');
        if (navItem === item) {
          navItemElement?.setAttribute('tabindex', '0');
          this.focusedElement = navItem;
          if (setFocus) {
            navItem.focus();
          }
        } else {
          navItemElement?.setAttribute('tabindex', '-1');
        }
      }, 1);
    });
  }
  /**
   * Calculates the width of the slot contents, so that remaining space can be used for primaryNav list
   */
  private slotWidth(slotName: string) {
    let slotWidth = 0;
    const slot = this.shadowRoot?.querySelector(`slot[name=${slotName}]`) as HTMLSlotElement;
    const slotContent = slot?.assignedNodes({ flatten: true }).filter((node) => node instanceof HTMLElement) as HTMLElement[];
    slotContent.forEach((element) => {
      slotWidth += element.clientWidth;
    });
    return slotWidth;
  }

  /**
   * 1) Make the PrimaryNav overflow as visible when any menu is opened
   */
  private onPrimaryNavItemPanelOpen() {
    if (!this.isMobile) {
      this.setAttribute('panelOpen', '');
    }
  }

  /**
   * 1) Make the PrimaryNav overflow as hidden when the menu is closed
   */
  private onPrimaryNavItemPanelClose() {
    if (!this.isMobile) {
      this.removeAttribute('panelOpen');
    }
  }

  /**
   * 1) Getting the hidden primary nav item on clicking on more list item
   */
  private moreListSelect(ev: MouseEvent) {
    const visibleList = (ev.target as BlocksListItem).parentElement?.querySelectorAll('.cat-is-visible')!;
    for (let i = 0; i < visibleList.length; i++) {
      if (visibleList[i] == ev.target) {
        this.primaryHiddenNavListItems[i]?.linkClickHandler(null);
      }
    }
  }

  private moreItems() {
    /**
     * Render the more item's dropdowns (up to 4 levels deep)
     * 1) If the primary  nav list item does not have an href (is a button), render the list item only
     * with text since there are no subitems
     * 2) Otherwise create an array for subItems to get pushed to and select all direct descendants of
     * the first dropdown panel's list
     * 3) If any of the items in the first dropdown have a list within them, then loop through those subitems and render the
     * second dropdown list. Otherwise, just render the inner text.
     * 4) If any of the subitems in the second dropdown have a list within them, loop through those subsubitems and render the
     * third dropdown list. Otherwise, just render the inner text.
     * 5) If any of the subsubitems in the third dropdown have a list within them, loop through those subsubsubitems and render
     * the last dropdown list. Otherwise, just render the inner text.
     */
    const itemTemplates = [];

    for (const child of this.primaryNavItems) {
      const slotBeforeItem = child.querySelector('[slot="before"]')?.cloneNode(true) as HTMLElement;
      const slotAfterItem = child.querySelector('[slot="after"]')?.cloneNode(true) as HTMLElement;
      if ((<BlocksPrimaryNavItem>child).href) {
        /* 1 */
        itemTemplates.push(html`
          <cat-list-item
            part="item"
            exportparts=${this.getExportParts('list-item-more', 'list-item')}
            ?isCurrent=${(<BlocksPrimaryNavItem>child).isCurrent}
            class="cat-c-primary-nav__more-dropdown-item"
            href=${ifDefined((<BlocksPrimaryNavItem>child).href)}
            @click=${this.moreListSelect}
          >
            ${slotBeforeItem}${(<BlocksPrimaryNavItem>child).text}${slotAfterItem}
          </cat-list-item>
        `);
      } else if ((<BlocksPrimaryNavItem>child).slots.hasSlotted('custom-link')) {
        const slot = child.shadowRoot?.querySelector('slot[name="custom-link"]') as HTMLSlotElement;
        if (slot) {
          const customLinkSlot = slot.assignedNodes({ flatten: false })[0];
          const customLinkElement = (customLinkSlot as HTMLElement).getAttribute('href');
          itemTemplates.push(html`
            <cat-list-item
              part="item"
              exportparts=${this.getExportParts('list-item-more', 'list-item')}
              ?isCurrent=${(<BlocksPrimaryNavItem>child).isCurrent}
              class="cat-c-primary-nav__more-dropdown-item"
            >
              <a part="part-custom-link" slot="custom-link" href="${ifDefined(customLinkElement ?? undefined)}">
                ${slotBeforeItem}${customLinkSlot.textContent}${slotAfterItem}</a
              >
            </cat-list-item>
          `);
        }
      } else {
        const subItemTemplates = []; /* 2 */
        const hasSubItems = (<BlocksPrimaryNavItem>child)
          .querySelector('cat-list')
          ?.querySelectorAll<BlocksListItem>(':scope > cat-list-item'); /* 2 */

        if (hasSubItems) {
          for (const subItem of Array.from(hasSubItems)) {
            if (subItem.querySelector('cat-list')) {
              const subSubItemTemplates = [];
              const hasSubSubItems = subItem.querySelector('cat-list')?.querySelectorAll<BlocksListItem>(':scope > cat-list-item');

              if (hasSubSubItems) {
                for (const subSubItem of Array.from(hasSubSubItems)) {
                  if (subSubItem.querySelector('cat-list')) {
                    const subSubSubItemTemplates = [];
                    const hasSubSubSubItems = subSubItem.querySelector('cat-list')?.querySelectorAll<BlocksListItem>(':scope > cat-list-item');
                    if (hasSubSubSubItems) {
                      for (const subSubSubItem of Array.from(hasSubSubSubItems)) {
                        subSubSubItemTemplates.push(html`
                          <cat-list-item
                            part="sub-sub-item"
                            exportparts=${this.getExportParts('list-item-sub-sub', 'list-item')}
                            ?isCurrent=${(<BlocksListItem>subSubSubItem).isCurrent}
                            >${subSubSubItem.innerText}</cat-list-item
                          >
                        `);
                      }
                    }

                    subSubItemTemplates.push(html`
                      <cat-list-item
                        part="item"
                        exportparts=${this.getExportParts('list-item-sub-sub-sub', 'list-item')}
                        behavior="${ifDefined(this.dropdownPanelBehavior)}"
                        ?isCurrent=${(<BlocksListItem>subSubItem).isCurrent}
                        dropdownPanelVariant="bare"
                      >
                        ${subSubItem.childNodes[0].textContent}
                        <cat-list
                          part="sublist-items"
                          exportparts=${this.getExportParts('list-sublist', 'list')}
                          class="cat-c-primary-nav__sublist"
                          slot="items"
                        >
                          ${subSubSubItemTemplates}
                        </cat-list>
                      </cat-list-item>
                    `); /* 5 */
                  } else {
                    subSubItemTemplates.push(html`
                      <cat-list-item
                        part="item"
                        exportparts=${this.getExportParts('list-item-sub-sub-sub', 'list-item')}
                        ?isCurrent=${(<BlocksListItem>subSubItem).isCurrent}
                      >
                        ${subSubItem.innerText}
                      </cat-list-item>
                    `);
                  }
                }
              }

              subItemTemplates.push(html`
                <cat-list-item
                  part="item"
                  exportparts=${this.getExportParts('list-item-sub-sub-sub', 'list-item')}
                  behavior="${ifDefined(this.dropdownPanelBehavior)}"
                  ?isCurrent=${(<BlocksListItem>subItem).isCurrent}
                  dropdownPanelVariant="bare"
                >
                  ${subItem.childNodes[0].textContent}
                  <cat-list
                    part="sublist-items"
                    exportparts=${this.getExportParts('list-sublist', 'list')}
                    class="cat-c-primary-nav__sublist"
                    slot="items"
                    >${subSubItemTemplates}
                  </cat-list>
                </cat-list-item>
              `); /* 4*/
            } else {
              subItemTemplates.push(html`
                <cat-list-item
                  part="item"
                  exportparts=${this.getExportParts('list-item-sub-sub-sub', 'list-item')}
                  ?isCurrent=${(<BlocksListItem>subItem).isCurrent}
                  href=${ifDefined(child.href)}
                >
                  ${subItem.innerText}
                </cat-list-item>
              `);
            }
          }
        }

        itemTemplates.push(html`
          <cat-list-item
            part="item"
            exportparts=${this.getExportParts('list-item-sub-sub-sub', 'list-item')}
            ?isCurrent=${(<BlocksPrimaryNavItem>child).isCurrent}
            behavior=${ifDefined(this.dropdownPanelBehavior)}
            dropdownPanelVariant="bare"
            class="cat-c-primary-nav__more-dropdown-item"
            @click=${this.moreListSelect}
          >
            ${slotBeforeItem}${(<BlocksPrimaryNavItem>child).text}${slotAfterItem}
            ${hasSubItems /* 3 */
              ? html`
                  <cat-list
                    part="sublist-items"
                    exportparts=${this.getExportParts('list-sublist', 'list')}
                    class="cat-c-primary-nav__sublist"
                    slot="items"
                  >
                    ${subItemTemplates}
                  </cat-list>
                `
              : nothing}
          </cat-list-item>
        `);
      }
    }

    return itemTemplates;
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-primary-nav', {
      'cat-is-collapsed': this.isCollapsed === true,
      'cat-is-mobile': this.isMobile === true,
      'cat-c-primary-nav--full-width': this.fullWidth === true
    });
    return html`
      <nav part="base" class="${componentClassName}" aria-label=${this.arialabel ? this.arialabel : 'Primary'} @keydown=${this.handleOnKeyDown}>
        <slot part="part-before" name="before"></slot>
        <ul part="list" role="menu" class="cat-c-primary-nav__list">
          <slot part="part-default" class="nav-list" @slotchange=${this.behavior === 'more' && !this.isMobile ? this.onSlotChange : nothing}></slot>
          ${this.behavior === 'more'
            ? html`
                <cat-primary-nav-item
                  part="more"
                  exportparts=${this.getExportParts('primary-nav-item')}
                  ?more=${true}
                  class="cat-c-primary-nav__more"
                  text=${this.moreDropdownLabel}
                  ?dropdown=${true}
                  dropdownAlign="left"
                >
                  <cat-list
                    part="more-dropdown"
                    exportparts=${this.getExportParts('list-more-dropdown', 'list')}
                    class="cat-c-primary-nav__more-dropdown"
                  >
                    ${this.moreItems()}
                  </cat-list>
                </cat-primary-nav-item>
              `
            : nothing}
        </ul>
        <slot part="part-after" name="after"></slot>
      </nav>
    `;
  }
}

if (customElements.get('cat-primary-nav') === undefined) {
  customElements.define('cat-primary-nav', BlocksPrimaryNav);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-primary-nav': BlocksPrimaryNav;
  }
}
